import React, {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useState,
} from 'react';
import { ignoreSessionUnavailable } from '@ais-ui/auth';
import { getEventsForPeriod, useSession } from '@ais-ui/websockets';
import { useEventNotification } from './useEventNotification';
import { parseEventSeverity, type Severity } from './eventSeverity';

export type Notification = {
  type: 'event';
  id: string;
  severity: Severity;
  mainText: string;
  secondaryText: string;
};

interface NotificationContextProps {
  notifications: Notification[];
  setNotifications: React.Dispatch<React.SetStateAction<Notification[]>>;
}

const NotificationContext = createContext<NotificationContextProps | null>(
  null,
);

export const useNotificationContext = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error(
      'useNotificationContext must be used within a NotificationProvider',
    );
  }
  return context;
};

interface NotificationProviderProps {
  children: ReactNode;
}

interface EventDetail {
  event_ids: string[];
}

/**
 * Notifications.tsx renders only the five most recent, and the list is
 * otherwise cleared in exactly one place: when the user closes the
 * notification popover. A session nobody touches - an unattended tab, a wall
 * display - therefore has no upper bound at all, so the list is capped here
 * instead of relying on a human being present to drain it.
 */
export const MAX_RETAINED_NOTIFICATIONS = 50;

export const appendNotification = (
  previous: Notification[],
  notification: Notification,
): Notification[] =>
  [...previous, notification].slice(-MAX_RETAINED_NOTIFICATIONS);

export const NotificationProvider: React.FC<NotificationProviderProps> = ({
  children,
}) => {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const { olSession } = useSession();

  // Function to update notifications
  const addNotification = (notification: Notification) => {
    setNotifications((prevNotifications) =>
      appendNotification(prevNotifications, notification),
    );
  };

  useEventNotification(olSession);

  useEffect(() => {
    const handleEventNotification = async (event: Event) => {
      const customEvent = event as CustomEvent<EventDetail>;

      const endDateTime = new Date();
      const startDateTime = new Date(endDateTime.getTime() - 5 * 60 * 1000);

      const data = await ignoreSessionUnavailable(
        getEventsForPeriod(olSession, startDateTime, endDateTime),
      );
      if (!data) {
        return;
      }

      for (const detectedEvent of data.events) {
        if (customEvent.detail.event_ids.includes(detectedEvent.id)) {
          const notification: Notification = {
            type: 'event',
            id: detectedEvent.id,
            severity: parseEventSeverity(detectedEvent.severity),
            mainText: detectedEvent.name,
            secondaryText: 'event',
          };

          addNotification(notification);
        }
      }
    };

    window.addEventListener('eventNotification', handleEventNotification);
    return () => {
      window.removeEventListener('eventNotification', handleEventNotification);
    };
  }, [olSession]);

  const value = {
    notifications,
    setNotifications,
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};
